import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Json } from "@/integrations/supabase/types";
import type { WebsitePageConfig } from "@/types/website-pages";

export interface WebsitePageRow {
  id: string;
  slug: string;
  title: string;
  meta_description: string | null;
  is_published: boolean;
  show_in_nav: boolean;
  nav_label: string | null;
  nav_order: number | null;
  page_type: string | null;
  page_config: WebsitePageConfig;
  updated_at: string;
}

export function useWebsitePagesAdmin() {
  return useQuery({
    queryKey: ["website-pages-admin"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("website_pages")
        .select("id, slug, title, meta_description, is_published, show_in_nav, nav_label, nav_order, page_type, page_config, updated_at")
        .order("slug");
      if (error) throw error;
      return (data ?? []).map((r) => ({
        ...r,
        page_config: (r.page_config ?? {}) as WebsitePageConfig,
      })) as WebsitePageRow[];
    },
  });
}

export interface UpdateWebsitePageInput {
  id: string;
  title?: string;
  meta_description?: string | null;
  page_config?: WebsitePageConfig;
}

export function useUpdateWebsitePage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateWebsitePageInput) => {
      const { id, page_config, ...patch } = input;
      const { error } = await supabase
        .from("website_pages")
        .update({
          ...patch,
          // WebsitePageConfig is structurally JSON; the generated column type
          // is Json, so cast at the boundary rather than widening the input type.
          ...(page_config !== undefined ? { page_config: page_config as unknown as Json } : {}),
          updated_at: new Date().toISOString(),
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["website-pages-admin"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
